/**
 * Error Boundary component for catching and handling React errors
 */
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button, Result } from 'antd';
import { secureLogger } from '../../utils/secureLogger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    secureLogger.error('React Error Boundary caught error', {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });

    this.props.onError?.(error, errorInfo);
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false, error: undefined });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Result
          status="error"
          title="アプリケーションエラー"
          subTitle="予期しないエラーが発生しました。ページを再読み込みしてください。"
          extra={[
            <Button type="primary" onClick={this.handleRetry} key="retry">
              再試行
            </Button>,
            <Button onClick={() => window.location.reload()} key="reload">
              ページ再読み込み
            </Button>,
          ]}
        />
      );
    }

    return this.props.children;
  }
}

// Hook version for functional components
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode
) => {
  return (props: P) => (
    <ErrorBoundary fallback={fallback}>
      <Component {...props} />
    </ErrorBoundary>
  );
};